import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, Bug, Sparkles } from 'lucide-react';
import { categorizeError, getErrorRecoveryActions, createErrorContext, isChunkLoadError } from '@/lib/errorHandling';
import { captureError } from '@/lib/sentry';
import { handleChunkError } from '@/lib/lazyWithRetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  context?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const context = createErrorContext('error_boundary', this.props.context);
    const categorized = categorizeError(error);
    const recovery = getErrorRecoveryActions(error, context);

    console.error('Error Boundary caught an error:', {
      error,
      errorInfo,
      context,
      categorization: categorized,
      recovery,
      errorId: this.state.errorId,
      componentStack: errorInfo.componentStack
    });

    this.setState({ errorInfo });

    // Handle chunk loading errors by attempting auto-reload
    if (isChunkLoadError(error)) {
      console.log('🔄 Chunk loading error detected in ErrorBoundary, attempting reload...');
      const reloaded = handleChunkError();
      if (reloaded) {
        // Page is reloading, don't proceed with error display
        return;
      }
      // If reload was already attempted, continue to show error UI
    }

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to Sentry (skip chunk errors as they're expected after deployments)
    if (!isChunkLoadError(error)) {
      this.logError(error, errorInfo, context);
    }
  }

  private logError = (error: Error, errorInfo: ErrorInfo, context: Record<string, unknown>) => {
    // Send error to Sentry with additional context
    captureError(error, {
      errorInfo,
      context,
      errorId: this.state.errorId,
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });

    // Also log to console for development
    console.group('Error Boundary Report');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Context:', context);
    console.error('Error ID:', this.state.errorId);
    console.error('User Agent:', navigator.userAgent);
    console.error('URL:', window.location.href);
    console.error('Timestamp:', new Date().toISOString());
    console.groupEnd();
  };

  private handleRetry = () => {
    this.setState(prev => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prev.retryCount + 1
    }));
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  private handleReportBug = () => {
    const { error, errorInfo, errorId } = this.state;
    const reportData = {
      errorId,
      error: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    };

    // In a real app, you would send this to your bug reporting service
    console.log('Bug Report Data:', reportData);
    
    // For now, just show an alert
    alert(`Error ID: ${errorId}\n\nPlease report this error to support with the error ID.`);
  };

  private getErrorSeverity = () => {
    if (!this.state.error) return 'medium';
    const categorized = categorizeError(this.state.error);
    return categorized.severity;
  };

  private getRecoveryActions = () => {
    if (!this.state.error) return [];
    
    const recovery = getErrorRecoveryActions(this.state.error);
    const actions = [];

    if (recovery.canRetry && recovery.retryAction) {
      actions.push({
        label: "Retry",
        onClick: recovery.retryAction,
        variant: "default" as const
      });
    }

    actions.push({
      label: "Go Home",
      onClick: this.handleGoHome,
      variant: "outline" as const
    });

    if (recovery.helpUrl) {
      actions.push({
        label: "Get Help",
        onClick: () => window.open(recovery.helpUrl, '_blank'),
        variant: "outline" as const
      });
    }

    return actions;
  };

  render() {
    if (this.state.hasError) {
      const { error, errorId, retryCount } = this.state;
      const severity = this.getErrorSeverity();
      const actions = this.getRecoveryActions();
      const isChunkError = error ? isChunkLoadError(error) : false;

      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Special UI for chunk loading errors (version updates)
      if (isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-950 p-4">
            <Card className="w-full max-w-md bg-gray-900 border-gray-800">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-blue-500/20">
                    <Sparkles className="h-8 w-8 text-blue-400" />
                  </div>
                </div>
                <CardTitle className="text-xl text-white">
                  Update Available
                </CardTitle>
                <CardDescription className="text-gray-400">
                  A new version of DeepFocus is available. Please refresh the page to continue.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500 text-center">
                  This happens when we deploy updates while you have the app open.
                </p>

                <Button
                  variant="default"
                  onClick={() => window.location.reload()}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh Page
                </Button>

                <Button
                  variant="outline"
                  onClick={this.handleGoHome}
                  className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Go to Dashboard
                </Button>
              </CardContent>
            </Card>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  severity === 'critical' ? 'bg-red-100' :
                  severity === 'high' ? 'bg-orange-100' :
                  severity === 'medium' ? 'bg-yellow-100' : 'bg-blue-100'
                }`}>
                  <AlertTriangle className={`h-8 w-8 ${
                    severity === 'critical' ? 'text-red-600' :
                    severity === 'high' ? 'text-orange-600' :
                    severity === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                  }`} />
                </div>
              </div>
              <CardTitle className="text-xl">
                {severity === 'critical' ? 'Critical Error' :
                 severity === 'high' ? 'Something went wrong' :
                 'Oops! Something went wrong'}
              </CardTitle>
              <CardDescription>
                {error?.message || 'An unexpected error occurred'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <p>Error ID: <code className="bg-muted px-1 rounded">{errorId}</code></p>
                {retryCount > 0 && (
                  <p>Retry attempts: {retryCount}</p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant}
                    onClick={action.onClick}
                    className="w-full"
                  >
                    {action.label === "Retry" && <RefreshCw className="mr-2 h-4 w-4" />}
                    {action.label === "Go Home" && <Home className="mr-2 h-4 w-4" />}
                    {action.label === "Get Help" && <Bug className="mr-2 h-4 w-4" />}
                    {action.label}
                  </Button>
                ))}
              </div>

              {severity === 'critical' && (
                <Button
                  variant="outline"
                  onClick={this.handleReportBug}
                  className="w-full"
                >
                  <Bug className="mr-2 h-4 w-4" />
                  Report Bug
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;